// Lexical-state lookup at a cursor offset. Mirrors the state machine in
// `src/features/sql-grammar/document/scanner.ts` but walks only as far as the
// cursor and returns the current class. Pure function, no tree-sitter.
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Position Classifier — core.
//
// Classifier contract: quoted-identifier regions (`"foo"` in PG/Oracle/SQLite/
// MSSQL, backticks in MySQL/SQLite, `[…]` in MSSQL/SQLite) count as `'code'`
// for position purposes — cursor inside a quoted column name should still
// classify against the surrounding clause, not bail out as `'string'`.

import type { ParseOptions } from '../../sql-grammar/model';

export type LexicalState = 'code' | 'string' | 'comment';

type RawState =
  | 'code'
  | 'line-comment'
  | 'block-comment'
  | 'single'
  | 'double'
  | 'backtick'
  | 'bracket'
  | 'dollar';

function doubleQuoteIsStringLiteral(options: ParseOptions): boolean {
  // MySQL: `"…"` is a string literal unless sql_mode has ANSI_QUOTES.
  // All other engines use `"…"` as an identifier quote.
  return options.engine === 'mysql' && options.ansiQuotes !== true;
}

function stateClass(raw: RawState, options: ParseOptions): LexicalState {
  if (raw === 'code') return 'code';
  if (raw === 'line-comment' || raw === 'block-comment') return 'comment';
  if (raw === 'single' || raw === 'dollar') return 'string';
  if (raw === 'double') return doubleQuoteIsStringLiteral(options) ? 'string' : 'code';
  return 'code';
}

// eslint-disable-next-line max-lines-per-function
export function lexicalStateAt(
  source: string,
  offset: number,
  options: ParseOptions,
): LexicalState {
  const engine = options.engine;
  const supportsBacktick = engine === 'mysql' || engine === 'sqlite';
  const supportsBracket = engine === 'mssql' || engine === 'sqlite';
  const supportsDollar = engine === 'postgres';
  const supportsHashComment = engine === 'mysql';

  const target = Math.min(Math.max(offset, 0), source.length);
  let state: RawState = 'code';
  let dollarTag = '';
  let i = 0;

  while (i < target) {
    const c = source[i];
    const c2 = source[i + 1] ?? '';
    if (state === 'code') {
      if (c === '-' && c2 === '-') { state = 'line-comment'; i += 2; continue; }
      if (supportsHashComment && c === '#') { state = 'line-comment'; i++; continue; }
      if (c === '/' && c2 === '*') { state = 'block-comment'; i += 2; continue; }
      if (c === "'") { state = 'single'; i++; continue; }
      if (c === '"') { state = 'double'; i++; continue; }
      if (supportsBacktick && c === '`') { state = 'backtick'; i++; continue; }
      if (supportsBracket && c === '[') { state = 'bracket'; i++; continue; }
      if (supportsDollar && c === '$') {
        const tag = source.slice(i).match(/^\$([A-Za-z_][A-Za-z0-9_]*)?\$/);
        if (tag) { state = 'dollar'; dollarTag = tag[0]; i += dollarTag.length; continue; }
      }
      i++;
    } else if (state === 'line-comment') {
      if (c === '\n') state = 'code';
      i++;
    } else if (state === 'block-comment') {
      if (c === '*' && c2 === '/') { state = 'code'; i += 2; } else i++;
    } else if (state === 'single') {
      if (c === "'") {
        if (c2 === "'") { i += 2; } else { state = 'code'; i++; }
      } else i++;
    } else if (state === 'double') {
      if (c === '"') {
        if (c2 === '"') { i += 2; } else { state = 'code'; i++; }
      } else i++;
    } else if (state === 'backtick') {
      if (c === '`') {
        if (c2 === '`') { i += 2; } else { state = 'code'; i++; }
      } else i++;
    } else if (state === 'bracket') {
      if (c === ']') {
        if (c2 === ']') { i += 2; } else { state = 'code'; i++; }
      } else i++;
    } else if (state === 'dollar') {
      if (source.startsWith(dollarTag, i)) { state = 'code'; i += dollarTag.length; } else i++;
    }
  }

  return stateClass(state, options);
}
